import * as R from 'ramda';
import {
  all,
  put,
  fork,
  call,
  take,
  delay,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
// components
import { closeModal } from '../../components/modal/actions';
import { closeLoader, openLoader } from '../../components/loader/actions';
// features
import { makeSelectCurrentBranchGuid } from '../branch/selectors';
import { getAccessorialForConfigRequest } from '../configurations/actions';
import { getLoadDetailsRequest, setDispatchPlannerOpened } from '../dispatch-details/load/actions';
import { getAllAvailableCloRefTypesRequest, getAllAvailableTelRefTypesRequest } from '../reference/actions';
import {
  checkReportFunction,
  transformSearchCriteriaBeforeReportPost,
} from '../../components/edit-report/helpers';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// sagas
import { visitPageSaga } from '../../sagas';
// utilities
import routesMap from '../../utilities/routes';
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
// feature dispatch-planner
import * as A from './actions';
import * as H from './helpers';
import { hasServerTripChanges } from './scheduler/reconcile-trip-details';
import {
  makeSelectTelList,
  makeSelectDraftList,
  makeSelectCloByGuid,
  makeSelectCloReport,
  makeSelectTelReport,
  makeSelectCurrentRoute,
  makeSelectBuilderStore,
  makeSelectSelectedTels,
  makeSelectRouteChanged,
  makeSelectInitRouteGuid,
  makeSelectCloPagination,
  makeSelectTelPagination,
  makeSelectOpenedFromPage,
  makeSelectDraftPagination,
  makeSelectInboundTelReport,
  makeSelectInboundTerminals,
  makeSelectOutboundTerminals,
  makeSelectOutboundTelReport,
  makeSelectDraftFilterParams,
  makeSelectCurrentRouteItems,
  makeSelectTelCreatedByFilter,
  makeSelectCloCreatedByFilter,
  makeSelectSplittedLoadOptions,
  makeSelectCloTableTitleFilters,
  makeSelectTelTableTitleFilters,
  makeSelectBranchGuidForRequest,
  makeSelectInboundTelPagination,
  makeSelectScheduleEditorHistory,
  makeSelectOutboundTelPagination,
  makeSelectCurrentRouteContainers,
  makeSelectCloTableTitleSortValues,
  makeSelectTelTableTitleSortValues,
  makeSelectCurrentRouteInitialState,
  makeSelectInboundTelCreatedByFilter,
  makeSelectOutboundTelCreatedByFilter,
  makeSelectInboundTelTableTitleFilters,
  makeSelectOutboundTelTableTitleFilters,
  makeSelectInboundTelTableTitleSortValues,
  makeSelectOutboundTelTableTitleSortValues,
  makeSelectPrimaryReferenceAutogeneratedConfig,
  // cross dock clos
  makeSelectCrossDockCloReport,
  makeSelectCrossDockCloByGuid,
  makeSelectCrossDockCloPagination,
  makeSelectCrossDockCloCreatedByFilter,
  makeSelectCrossDockCloTableTitleFilters,
  makeSelectCrossDockCloTableTitleSortValues,
} from './selectors';
//////////////////////////////////////////////////

function* resetScheduleHistoryAfterExternalMutation() {
  const { undoStack = [], redoStack = [] } = yield select(makeSelectScheduleEditorHistory());

  if (R.isEmpty(undoStack) && R.isEmpty(redoStack)) return;

  yield put(A.resetScheduleHistory());

  const message = G.getWindowLocale(
    'messages:schedule-editor-history-reset',
    'This server action is complete. Earlier Schedule Editor changes can no longer be undone.',
  );

  yield call(G.showToastrMessageSimple, 'warning', message);
}

function* handleGetRouteByGuid(guid) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const res = yield call(sendRequest, 'get', endpointsMap.getRouteByGuidEndpoint(guid));

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.setAvailableItems(H.mapRouteItems(data.tels)));
      yield put(A.setAvailableContainers(H.mapRouteContainers(data.tels)));
      yield put(A.setCurrentRoute(H.convertRouteResponseWithOrigRouteGuid(data)));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetRouteByGuid fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleGetRouteByGuid exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleGetCloDetailsRequest(guid) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const clo = yield select(makeSelectCloByGuid(guid));
    const crossDockClo = yield select(makeSelectCrossDockCloByGuid(guid));

    const details = R.pathOr(
      R.path(['details'], crossDockClo),
      ['details'],
      clo,
    );

    if (G.isNotNilAndNotEmpty(details)) {
      yield put(A.getCloDetailsSuccess(details));
      yield put(closeLoader());

      return;
    }

    const params = { payload: guid };

    const res = yield call(sendRequest, 'get', endpointsMap.getRouteBuilderCloDetailsEndpoint(guid), { params });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getCloDetailsSuccess(data));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetCloDetailsRequest fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleGetCloDetailsRequest exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleSelectCloForTelRequest({ payload }) {
  const cloGuid = R.path(['returnObject', GC.FIELD_CLO_GUID], payload);
  const telGuid = R.path(['selectedValue', GC.FIELD_VALUE], payload);

  try {
    if (R.and(G.isNotNilAndNotEmpty(cloGuid), G.isNotNilAndNotEmpty(telGuid))) {
      yield put(openLoader({ showDimmer: true }));

      yield call(handleGetCloDetailsRequest, cloGuid);

      const clo = yield select(makeSelectCloByGuid(cloGuid));

      if (G.isNotNilAndNotEmpty(R.prop('details', clo))) {
        if (payload.captureScheduleHistory) yield put(A.captureScheduleHistory());

        yield put(A.selectCloForTelSuccess({ telGuid, cloDetails: clo.details }));

        yield delay(100);

        yield put(A.recalculateTelDistancesRequest(telGuid));
      }
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleSelectCloForTelRequest exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleSelectCrossDockCloSaga({ payload }) {
  const cloGuid = R.path(['returnObject', GC.FIELD_CLO_GUID], payload);
  const telGuid = R.path(['selectedValue', GC.FIELD_VALUE], payload);

  try {
    if (R.and(G.isNotNilAndNotEmpty(cloGuid), G.isNotNilAndNotEmpty(telGuid))) {
      yield put(openLoader({ showDimmer: true }));

      yield call(handleGetCloDetailsRequest, cloGuid);

      const clo = yield select(makeSelectCrossDockCloByGuid(cloGuid));

      if (G.isNotNilAndNotEmpty(R.prop('details', clo))) {
        yield put(A.selectCrossDockCloSuccess({ telGuid, cloDetails: clo.details }));
      }
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleSelectCrossDockCloSaga exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleToggleCloDetails({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    yield call(handleGetCloDetailsRequest, payload);

    yield put(A.toggleClo(payload));
  } catch (err) {
    yield call(G.handleException, err, 'handleToggleCloDetails exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleToggleCrossDockCloDetails({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    yield call(handleGetCloDetailsRequest, payload);

    yield put(A.toggleCrossDockClo(payload));
  } catch (err) {
    yield call(G.handleException, err, 'handleToggleCrossDockCloDetails exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleToggleTelDetails({ payload }) {
  const { guid } = payload;

  yield put(A.toggleTel(payload));

  if (G.isNilOrEmpty(R.prop('details', payload))) {
    try {
      yield put(openLoader({ showDimmer: true }));

      const res = yield call(sendRequest, 'get', endpointsMap.getTelEndpoint(guid), { params: { guid } });

      const { data, status } = res;

      if (G.isResponseSuccess(status)) {
        yield put(A.setTelDetails(data));
      } else {
        yield call(G.handleFailResponse, res, 'handleToggleTelDetails fail');
      }

      yield put(closeLoader());
    } catch (err) {
      yield put(closeLoader());

      yield call(G.handleException, err, 'handleToggleTelDetails exception');
    }
  }
}

function* handleGetActiveCloListSaga(report) {
  try {
    yield put(A.setCloListLoading(true));

    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    const pagination = {
      limit: 100,
      offset: 0,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
    };

    const options = { data: R.mergeRight(report, pagination) };

    const res = yield call(sendRequest, 'post', endpointsMap.cloListRouteBuilder, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.setSelectedCloList(data));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetActiveCloListSaga fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleGetActiveCloListSaga exception');
  } finally {
    yield put(A.setCloListLoading(false));
  }
}

function* handleGetActiveCrossDockCloListSaga(report) {
  try {
    const config = G.getAmousConfigByNameFromWindow(GC.CLO_GENERAL_USE_LINKED_ORDERS);

    if (R.not(config)) return;

    yield put(A.setCrossDockCloListLoading(true));

    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    const pagination = {
      offset: 0,
      limit: 100,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
    };

    const options = { data: R.mergeRight(report, pagination) };

    const res = yield call(sendRequest, 'post', endpointsMap.cloListRouteBuilder, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.setSelectedCrossDockCloList(data));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetActiveCrossDockCloListSaga fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleGetActiveCrossDockCloListSaga exception');
  } finally {
    yield put(A.setCrossDockCloListLoading(false));
  }
}

function* handleGetCloListSaga(isInitial) {
  try {
    yield put(A.setCloListLoading(true));

    const pagination = yield select(makeSelectCloPagination());

    const { total, limit, offset } = pagination;

    if (R.gte(offset, total)) return;

    const report = yield select(makeSelectCloReport());
    const createdBy = yield select(makeSelectCloCreatedByFilter());
    const titleFilterParams = yield select(makeSelectCloTableTitleFilters());
    const titleOrderFields = yield select(makeSelectCloTableTitleSortValues());
    const branchGuidForRequest = yield select(makeSelectBranchGuidForRequest());

    let currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    if (G.isNotNilAndNotEmpty(branchGuidForRequest)) currentBranchGuid = branchGuidForRequest;

    const transformedSearchCriteria = transformSearchCriteriaBeforeReportPost(
      G.getSearchCriteriaWithTitleFilters(report, titleFilterParams),
    );

    const reqSearchCriteria = G.getSearchCriteriaWithCreatedByFilter(createdBy, transformedSearchCriteria);

    const reqBody = {
      limit,
      offset,
      searchCriteria: reqSearchCriteria,
      fields: G.getOrElse(report, 'fields', []),
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      orderFields: G.ifElse(
        G.isNotEmpty(titleOrderFields),
        R.values(titleOrderFields),
        G.getOrElse(report, 'orderFields', []),
      ),
    };

    const options = {
      data: reqBody,
    };

    const res = yield call(sendRequest, 'post', endpointsMap.cloListRouteBuilder, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.setCloList(data));

      const { offset } = yield select(makeSelectCloPagination());

      if (R.and(R.gt(G.getPropFromObject('totalCount', data), offset), G.isTrue(isInitial))) {
        yield put(A.getCloNextPage());
      }
    } else {
      yield call(G.handleFailResponse, res, 'handleGetCloListSaga fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleGetCloListSaga exception');
  } finally {
    yield put(A.setCloListLoading(false));
  }
}

function* handleGetCrossDockCloListSaga(isInitial) {
  try {
    const config = G.getAmousConfigByNameFromWindow(GC.CLO_GENERAL_USE_LINKED_ORDERS);

    if (R.not(config)) return;

    yield put(A.setCrossDockCloListLoading(true));

    const pagination = yield select(makeSelectCrossDockCloPagination());

    const { total, limit, offset } = pagination;

    if (R.gte(offset, total)) return;

    const report = yield select(makeSelectCrossDockCloReport());
    const createdBy = yield select(makeSelectCrossDockCloCreatedByFilter());
    const branchGuidForRequest = yield select(makeSelectBranchGuidForRequest());
    const titleFilterParams = yield select(makeSelectCrossDockCloTableTitleFilters());
    const titleOrderFields = yield select(makeSelectCrossDockCloTableTitleSortValues());

    let currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    if (G.isNotNilAndNotEmpty(branchGuidForRequest)) currentBranchGuid = branchGuidForRequest;

    const transformedSearchCriteria = transformSearchCriteriaBeforeReportPost(
      G.getSearchCriteriaWithTitleFilters(report, titleFilterParams),
    );

    const reqSearchCriteria = G.getSearchCriteriaWithCreatedByFilter(createdBy, transformedSearchCriteria);

    const reqBody = {
      limit,
      offset,
      searchCriteria: reqSearchCriteria,
      fields: G.getOrElse(report, 'fields', []),
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      orderFields: G.ifElse(
        G.isNotEmpty(titleOrderFields),
        R.values(titleOrderFields),
        G.getOrElse(report, 'orderFields', []),
      ),
    };

    const options = {
      data: reqBody,
    };

    const res = yield call(sendRequest, 'post', endpointsMap.cloListRouteBuilder, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.setCrossDockCloList(data));

      const { offset } = yield select(makeSelectCrossDockCloPagination());

      if (R.and(R.gt(G.getPropFromObject('totalCount', data), offset), G.isTrue(isInitial))) {
        yield put(A.getCrossDockCloNextPage());
      }
    } else {
      yield call(G.handleFailResponse, res, 'handleGetCrossDockCloListSaga fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleGetCrossDockCloListSaga exception');
  } finally {
    yield put(A.setCrossDockCloListLoading(false));
  }
}

function* handleGetActiveTelListSaga(report) {
  try {
    yield put(A.setTelListLoading(true));

    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    const props = {
      limit: 100,
      offset: 0,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
    };

    const options = { data: R.mergeRight(report, props) };

    const res = yield call(sendRequest, 'post', endpointsMap.telListRouteBuilder, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.setSelectedTelList(data));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetActiveTelListSaga fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleGetActiveTelListSaga exception');
  } finally {
    yield put(A.setTelListLoading(false));
  }
}

function* getReportTelByGuid({ payload }) {
  try {
    yield put(A.setTelListLoading(true));

    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    const report = yield select(makeSelectTelReport());

    const props = {
      offset: 0,
      limit: 100,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      searchCriteria: R.of(Array, {
        dataType: 'string',
        operation: 'equal',
        stringValue: payload,
        propertyName: GC.FIELD_GUID,
      }),
    };

    const options = { data: R.mergeRight(report, props) };

    const res = yield call(sendRequest, 'post', endpointsMap.telListRouteBuilder, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.prependTelToList(data));
    } else {
      yield call(G.handleFailResponse, res, 'getReportTelByGuid fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'getReportTelByGuid exception');
  } finally {
    yield put(A.setTelListLoading(false));
  }
}

function* handleGetDraftListSaga({ payload }) {
  try {
    yield put(A.setDraftListLoading(true));

    const { showOnlyOwn } = payload;

    const draftList = yield select(makeSelectDraftList());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());
    const pagination = yield select(makeSelectDraftPagination());
    const filterParams = yield select(makeSelectDraftFilterParams());

    const reqBody = {
      showOnlyOwn,
      searchCriteria: {},
      limit: pagination.limit,
      offset: pagination.offset,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
    };

    const options = { data: G.setSearchCriteria({ filterParams, reqBody }) };

    const res = yield call(sendRequest, 'post', endpointsMap.draftRouteList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(
        A.getDraftListSuccess({
          totalCount: data.totalCount,
          results: H.mapDraftsForInternalUsage(data.results, draftList.length),
        }),
      );
    } else {
      yield call(G.handleFailResponse, res, 'handleGetDraftListSaga fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleGetDraftListSaga exception');
  } finally {
    yield put(A.setDraftListLoading(false));
  }
}

function* handleGetTelListSaga({ payload }) {
  try {
    yield put(A.setTelListLoading(true));

    const report = yield select(makeSelectTelReport());
    const pagination = yield select(makeSelectTelPagination());
    const createdBy = yield select(makeSelectTelCreatedByFilter());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());
    const titleFilterParams = yield select(makeSelectTelTableTitleFilters());
    const titleOrderFields = yield select(makeSelectTelTableTitleSortValues());

    const { limit, offset } = pagination;

    const transformedSearchCriteria = transformSearchCriteriaBeforeReportPost(
      G.getSearchCriteriaWithTitleFilters(report, titleFilterParams),
    );

    const reqSearchCriteria = G.getSearchCriteriaWithCreatedByFilter(createdBy, transformedSearchCriteria);

    const reqBody = {
      limit,
      offset,
      searchCriteria: reqSearchCriteria,
      fields: G.getOrElse(report, 'fields', []),
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      orderFields: G.ifElse(
        G.isNotEmpty(titleOrderFields),
        R.values(titleOrderFields),
        G.getOrElse(report, 'orderFields', []),
      ),
    };

    const options = {
      data: reqBody,
    };

    const res = yield call(sendRequest, 'post', endpointsMap.telListRouteBuilder, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.setTelList(data));

      const { offset } = yield select(makeSelectTelPagination());

      if (R.and(R.gt(G.getPropFromObject('totalCount', data), offset), G.isTrue(payload))) {
        yield put(A.getTelNextPage());
      }
    } else {
      yield call(G.handleFailResponse, res, 'handleGetTelListSaga fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleGetTelListSaga exception');
  } finally {
    yield put(A.setTelListLoading(false));
  }
}

function* handleGetInboundTelListSaga({ payload }) {
  try {
    yield put(A.setInboundTelListLoading(true));

    const report = yield select(makeSelectInboundTelReport());
    const pagination = yield select(makeSelectInboundTelPagination());
    const terminalDropIds = yield select(makeSelectInboundTerminals());
    const createdBy = yield select(makeSelectInboundTelCreatedByFilter());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());
    const titleFilterParams = yield select(makeSelectInboundTelTableTitleFilters());
    const titleOrderFields = yield select(makeSelectInboundTelTableTitleSortValues());

    const { limit, offset } = pagination;

    const transformedSearchCriteria = transformSearchCriteriaBeforeReportPost(
      G.getSearchCriteriaWithTitleFilters(report, titleFilterParams),
    );

    const reqSearchCriteria = G.getSearchCriteriaWithCreatedByFilter(createdBy, transformedSearchCriteria);

    const reqBody = {
      limit,
      offset,
      terminalDropIds,
      searchCriteria: reqSearchCriteria,
      fields: G.getOrElse(report, 'fields', []),
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      orderFields: G.ifElse(
        G.isNotEmpty(titleOrderFields),
        R.values(titleOrderFields),
        G.getOrElse(report, 'orderFields', []),
      ),
    };

    const options = {
      data: reqBody,
    };

    const res = yield call(sendRequest, 'post', endpointsMap.telListRouteBuilder, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.setInboundTelList(data));

      const { offset } = yield select(makeSelectInboundTelPagination());

      if (R.and(R.gt(G.getPropFromObject('totalCount', data), offset), G.isTrue(payload))) {
        yield put(A.getInboundTelNextPage());
      }
    } else {
      yield call(G.handleFailResponse, res, 'handleGetInboundTelListSaga fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleGetInboundTelListSaga exception');
  } finally {
    yield put(A.setInboundTelListLoading(false));
  }
}

function* handleGetOutboundTelListSaga({ payload }) {
  try {
    yield put(A.setOutboundTelListLoading(true));

    const report = yield select(makeSelectOutboundTelReport());
    const pagination = yield select(makeSelectOutboundTelPagination());
    const terminalPickupIds = yield select(makeSelectOutboundTerminals());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());
    const createdBy = yield select(makeSelectOutboundTelCreatedByFilter());
    const titleFilterParams = yield select(makeSelectOutboundTelTableTitleFilters());
    const titleOrderFields = yield select(makeSelectOutboundTelTableTitleSortValues());

    const { limit, offset } = pagination;

    const transformedSearchCriteria = transformSearchCriteriaBeforeReportPost(
      G.getSearchCriteriaWithTitleFilters(report, titleFilterParams),
    );

    const reqSearchCriteria = G.getSearchCriteriaWithCreatedByFilter(createdBy, transformedSearchCriteria);

    const reqBody = {
      limit,
      offset,
      terminalPickupIds,
      searchCriteria: reqSearchCriteria,
      fields: G.getOrElse(report, 'fields', []),
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      orderFields: G.ifElse(
        G.isNotEmpty(titleOrderFields),
        R.values(titleOrderFields),
        G.getOrElse(report, 'orderFields', []),
      ),
    };

    const options = {
      data: reqBody,
    };

    const res = yield call(sendRequest, 'post', endpointsMap.telListRouteBuilder, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.setOutboundTelList(data));

      const { offset } = yield select(makeSelectOutboundTelPagination());

      if (R.and(R.gt(G.getPropFromObject('totalCount', data), offset), G.isTrue(payload))) {
        yield put(A.getOutboundTelNextPage());
      }
    } else {
      yield call(G.handleFailResponse, res, 'handleGetOutboundTelListSaga fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleGetOutboundTelListSaga exception');
  } finally {
    yield put(A.setOutboundTelListLoading(false));
  }
}

function* handleRefreshCloListWithRouteClos() {
  try {
    const cloReport = yield select(makeSelectCloReport());
    const currentRoute = yield select(makeSelectCurrentRoute());

    const clos = R.values(currentRoute.clos);

    yield put(A.resetCloListAndPagination());

    if (G.isNotNilAndNotEmpty(clos)) {
      const reportData = R.assoc(
        GC.FIELD_SEARCH_CRITERIA,
        R.of(Array, {
          operation: 'in',
          dataType: 'string',
          propertyName: GC.FIELD_GUID,
          stringValue: R.join(',', R.map(({ guid }) => guid, clos)),
        }),
        cloReport,
      );

      yield call(handleGetActiveCloListSaga, reportData);
    }

    yield call(handleGetCloListSaga, true);
  } catch (err) {
    yield call(G.handleException, err, 'handleRefreshCloListWithRouteClos exception');
  }
}

function* handleRefreshCrossDockCloListWithRouteClos() {
  try {
    const report = yield select(makeSelectCrossDockCloReport());
    const currentRoute = yield select(makeSelectCurrentRoute());

    const clos = R.values(currentRoute.linkedClos);

    yield put(A.resetCrossDockCloListAndPagination());

    if (G.isNotNilAndNotEmpty(clos)) {
      const reportData = R.assoc(
        GC.FIELD_SEARCH_CRITERIA,
        R.of(Array, {
          operation: 'in',
          dataType: 'string',
          propertyName: GC.FIELD_GUID,
          stringValue: R.join(',', R.map(({ guid }) => guid, clos)),
        }),
        report,
      );

      yield call(handleGetActiveCrossDockCloListSaga, reportData);
    }

    yield call(handleGetCrossDockCloListSaga, true);
  } catch (err) {
    yield call(G.handleException, err, 'handleRefreshCrossDockCloListWithRouteClos exception');
  }
}

function* handleGetDraftRouteByDraftGuid({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const params = { draftRouteGuid: R.path(['draft', 'draftGuid'], payload) };

    const res = yield call(sendRequest, 'get', endpointsMap.draftRoute, { params });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.setAvailableItems(H.mapRouteItems(data.tels)));
      yield put(A.setCurrentRoute(H.convertDraftRouteResponse(data)));

      if (G.isNotNilAndNotEmpty(data.clos)) {
        yield call(handleRefreshCloListWithRouteClos, { clos: data.clos });
      }
    } else {
      yield call(G.handleFailResponse, res, 'handleGetDraftRouteByDraftGuid fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleGetDraftRouteByDraftGuid exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleRefreshCloListWithRouteGuid({ route }) {
  try {
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());
    const report = yield select(makeSelectCloReport());

    const reportData = H.getInitialReportFromResponse(
      R.of(Array, report),
      currentBranchGuid,
      route.guid,
    );

    yield put(A.resetCloListAndPagination());
    yield put(A.setCloListLoading(false));

    yield call(handleGetActiveCloListSaga, reportData.withSearchCriteria);
    yield call(handleGetCloListSaga, true);
  } catch (err) {
    yield call(G.handleException, err, 'handleRefreshCloListWithRouteGuid exception');
  }
}

function* handleGetRouteByTelGuid({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const res = yield call(sendRequest, 'get', endpointsMap.route, { params: { telGuid: payload } });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.selectTel(R.map(
        ({ guid }) => guid,
        data.tels,
      )));

      yield put(A.setAvailableItems(H.mapRouteItems(data.tels)));
      yield put(A.setCurrentRoute(H.convertRouteResponseWithOrigRouteGuid(data)));

      yield call(handleRefreshCloListWithRouteGuid, { route: data });

      if (G.isNotNilAndNotEmpty(data.linkedClos)) {
        yield call(handleRefreshCrossDockCloListWithRouteClos);
      }
    } else {
      yield call(G.handleFailResponse, res, 'handleGetRouteByTelGuid fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleGetRouteByTelGuid exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleReconcileTripDetails({ payload: telGuid }) {
  try {
    const currentRoute = yield select(makeSelectCurrentRoute());
    const baselineRoute = yield select(makeSelectCurrentRouteInitialState());

    if (!R.path(['tels', telGuid], currentRoute)) return;

    yield put(openLoader());

    const res = yield call(sendRequest, 'get', endpointsMap.route, {
      params: { telGuid },
    });

    const { data, status } = res;

    if (!G.isResponseSuccess(status)) {
      yield call(G.handleFailResponse, res, 'handleReconcileTripDetails fail');

      return;
    }

    const rawTel = R.find((tel) => tel.guid === telGuid, data.tels || []);

    if (!rawTel) return;

    const route = H.convertRouteResponseWithOrigRouteGuid(data);

    const sameSavedRoute = !currentRoute.draftGuid
      && currentRoute.guid
      && currentRoute.guid === route.guid;

    const sourceTels = sameSavedRoute ? data.tels : [rawTel];
    const items = H.mapRouteItems(sourceTels);
    const containers = H.mapRouteContainers(sourceTels);
    const baselineTel = R.path(['tels', telGuid], baselineRoute);
    const serverTel = R.path(['tels', telGuid], route);

    const changed = sameSavedRoute
      ? !R.equals(route, baselineRoute)
      : hasServerTripChanges(baselineTel, serverTel);

    if (!changed) return;

    yield put(A.reconcileTripDetailsSuccess({ telGuid, route, items, containers }));
    yield call(resetScheduleHistoryAfterExternalMutation);
  } catch (error) {
    yield call(G.handleException, error, 'handleReconcileTripDetails exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleGetRouteByTelGuidAndMergeSaga({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const res = yield call(sendRequest, 'get', endpointsMap.route, { params: { telGuid: payload } });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const currentRoute = yield select(makeSelectCurrentRoute());
      const selectedTels = yield select(makeSelectSelectedTels());

      const newSelectedTels = R.concat(R.values(selectedTels), R.map(
        ({ guid }) => guid,
        data.tels,
      ));

      yield put(A.selectTelOnAllLists(newSelectedTels));

      const currentItems = yield select(makeSelectCurrentRouteItems());

      yield put(A.setAvailableItems(R.mergeRight(currentItems, H.mapRouteItems(data.tels))));

      const currentContainers = yield select(makeSelectCurrentRouteContainers());

      yield put(A.setAvailableContainers(R.mergeRight(currentContainers, H.mapRouteContainers(data.tels))));

      const currentTels = currentRoute[GC.SYSTEM_LIST_TELS];

      const condition = R.or(
        R.equals(R.length(R.values(currentTels)), 0),
        R.and(
          R.equals(R.length(R.values(currentTels)), 1),
          R.equals(R.length(R.values(R.path([0, GC.FIELD_LOAD_STOPS], R.values(currentTels)))), 0),
        ),
      );

      if (condition) {
        yield put(A.setCurrentRoute(H.convertRouteResponseWithOrigRouteGuid(data)));
      } else {
        const incomeRoute = H.convertRouteResponseWithOrigRouteGuid(
          data,
          R.length(R.values(currentRoute[GC.SYSTEM_LIST_TELS])),
        );

        const newRoute = R.mergeRight(
          currentRoute,
          {
            linkedClos: R.mergeRight(incomeRoute.linkedClos, currentRoute.linkedClos),
            [GC.SYSTEM_LIST_TELS]: R.mergeRight(currentTels, incomeRoute[GC.SYSTEM_LIST_TELS]),
            [GC.SYSTEM_LIST_CLOS]: R.mergeRight(currentRoute[GC.SYSTEM_LIST_CLOS], incomeRoute[GC.SYSTEM_LIST_CLOS]),
          },
        );

        yield put(A.setCurrentRoute(newRoute));
      }

      yield call(handleRefreshCloListWithRouteClos);
      yield call(handleRefreshCrossDockCloListWithRouteClos);
    } else {
      yield call(G.handleFailResponse, res, 'handleGetRouteByTelGuidAndMergeSaga fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleGetRouteByTelGuidAndMergeSaga exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleGetAvailableCloReportSaga({ params, currentBranchGuid, routeGuid, notCallLists }) {
  try {
    const res = yield call(sendRequest, 'get', endpointsMap.listReports, { params });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const report = checkReportFunction(data);
      const reportData = H.getInitialReportFromResponse(report, currentBranchGuid, routeGuid);

      if (R.not(reportData.report)) {
        yield put(A.getAvailableCloReportSuccess());

        return;
      }

      yield put(A.setAvailableCloReports(report));
      yield put(A.setCurrentCloReport(reportData.report));
      yield put(A.setCurrentCrossDockCloReport(reportData.report));

      if (R.is(Object, reportData.withSearchCriteria)) {
        yield call(handleGetActiveCloListSaga, reportData.withSearchCriteria);
        yield call(handleRefreshCrossDockCloListWithRouteClos);
      }

      if (R.or(G.isNotNil(routeGuid), R.not(G.isTrue(notCallLists)))) {
        yield call(handleGetCloListSaga, true);
      }

      yield put(A.getAvailableCloReportSuccess());
    } else {
      yield call(G.handleFailResponse, res, 'handleGetAvailableCloReportSaga fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleGetAvailableCloReportSaga exception');
  }
}

function* handleCreateCloReportRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const routeGuid = yield select(makeSelectInitRouteGuid());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    const searchCriteria = transformSearchCriteriaBeforeReportPost(R.path([GC.FIELD_SEARCH_CRITERIA], payload));
    const newPayload = R.set(R.lensProp(GC.FIELD_SEARCH_CRITERIA), searchCriteria, payload);

    const params = {
      reportType: GC.CLO_REPORT,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
    };

    const options = { data: R.assoc(GC.FIELD_BRANCH_GUID, currentBranchGuid, newPayload) };

    const res = yield call(sendRequest, 'post', endpointsMap.report, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(
        handleGetAvailableCloReportSaga,
        { params, currentBranchGuid, routeGuid, notCallLists: true },
      );

      yield put(A.setCurrentCloReport(data));

      const currentRoute = yield select(makeSelectCurrentRoute());

      if (G.isNotNilAndNotEmpty(currentRoute.clos)) {
        yield call(handleRefreshCloListWithRouteClos);
      } else {
        yield call(handleGetCloListSaga, true);
      }

      if (G.isNotNilAndNotEmpty(currentRoute.linkedClos)) {
        yield call(handleRefreshCrossDockCloListWithRouteClos);
      } else {
        yield call(handleGetCrossDockCloListSaga, true);
      }
    } else {
      yield call(G.handleFailResponse, res, 'handleCreateCloReportRequestSaga fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleCreateCloReportRequestSaga exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleUpdateCloReportRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const routeGuid = yield select(makeSelectInitRouteGuid());

    const searchCriteria = transformSearchCriteriaBeforeReportPost(R.path([GC.FIELD_SEARCH_CRITERIA], payload));
    const options = { data: R.set(R.lensProp(GC.FIELD_SEARCH_CRITERIA), searchCriteria, payload) };

    const res = yield call(sendRequest, 'put', endpointsMap.report, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

      const params = {
        reportType: GC.CLO_REPORT,
        [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      };

      yield call(
        handleGetAvailableCloReportSaga,
        { params, currentBranchGuid, routeGuid, notCallLists: true },
      );

      const currentRoute = yield select(makeSelectCurrentRoute());

      if (G.isNotNilAndNotEmpty(currentRoute.clos)) {
        yield call(handleRefreshCloListWithRouteClos);
      } else {
        yield call(handleGetCloListSaga, true);
      }

      if (G.isNotNilAndNotEmpty(currentRoute.linkedClos)) {
        yield call(handleRefreshCrossDockCloListWithRouteClos);
      } else {
        yield call(handleGetCrossDockCloListSaga, true);
      }
    } else {
      yield call(G.handleFailResponse, res, 'handleUpdateCloReportRequestSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleUpdateCloReportRequestSaga exception');
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
  } finally {
    yield put(closeLoader());
  }
}

function* handleGetAvailableTelReportSaga({ params, currentBranchGuid, routeGuid, initRouteGuid }) {
  try {
    const res = yield call(sendRequest, 'get', endpointsMap.listReports, { params });

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const report = checkReportFunction(data);
      const reportData = H.getInitialReportFromResponse(report, currentBranchGuid, routeGuid);

      if (R.not(reportData.report)) return;

      yield put(A.setAvailableTelReports(report));

      if (initRouteGuid) {
        yield put(A.setCurrentTelReport(reportData.report));
      } else {
        yield put(A.setCurrentTelReportWithRoute(reportData.report));
      }

      yield put(A.setCurrentInboundTelReport(reportData.report));
      yield put(A.setCurrentOutboundTelReport(reportData.report));

      if (R.is(Object, reportData.withSearchCriteria)) {
        yield call(handleGetActiveTelListSaga, reportData.withSearchCriteria);
      }
    } else {
      yield call(G.handleFailResponse, res, 'handleGetAvailableTelReportSaga fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleGetAvailableTelReportSaga exception');
  }
}

function* handleCreateTelReportRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const routeGuid = yield select(makeSelectInitRouteGuid());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    const params = {
      reportType: GC.TEL_REPORT,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
    };

    const searchCriteria = transformSearchCriteriaBeforeReportPost(R.path([GC.FIELD_SEARCH_CRITERIA], payload));
    const newPayload = R.set(R.lensProp(GC.FIELD_SEARCH_CRITERIA), searchCriteria, payload);
    const options = { data: R.assoc(GC.FIELD_BRANCH_GUID, currentBranchGuid, newPayload) };

    const res = yield call(sendRequest, 'post', endpointsMap.report, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(
        handleGetAvailableTelReportSaga,
        { params, currentBranchGuid, routeGuid },
      );
    } else {
      yield call(G.handleFailResponse, res, 'handleCreateTelReportRequestSaga fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleCreateTelReportRequestSaga exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleUpdateTelReportRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const routeGuid = yield select(makeSelectInitRouteGuid());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    const params = {
      reportType: GC.TEL_REPORT,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
    };

    const searchCriteria = transformSearchCriteriaBeforeReportPost(R.path([GC.FIELD_SEARCH_CRITERIA], payload));
    const options = { data: R.set(R.lensProp(GC.FIELD_SEARCH_CRITERIA), searchCriteria, payload) };

    const res = yield call(sendRequest, 'put', endpointsMap.report, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(
        handleGetAvailableTelReportSaga,
        { params, currentBranchGuid, routeGuid },
      );
    } else {
      yield call(G.handleFailResponse, res, 'handleUpdateTelReportRequestSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleUpdateTelReportRequestSaga exception');
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
  } finally {
    yield put(closeLoader());
  }
}

function* handleAvailableReportRequestSaga({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const routeGuid = yield select(makeSelectInitRouteGuid());
    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    const cloParams = {
      reportType: GC.CLO_REPORT,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
    };

    const telParams = {
      reportType: GC.TEL_REPORT,
      [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
    };

    const notOnlyClo = R.not(R.path(['onlyClo'], payload));

    yield put(A.setRouteBuilderInitialState());
    yield put(A.setInitialCurrentRoute(currentBranchGuid));

    if (G.isNotNilAndNotEmpty(routeGuid)) {
      yield call(handleGetRouteByGuid, routeGuid);
    }

    const openedFromPage = yield select(makeSelectOpenedFromPage());

    if (R.isNil(openedFromPage)) {
      yield fork(
        handleGetAvailableCloReportSaga,
        { params: cloParams, currentBranchGuid, routeGuid },
      );

      if (notOnlyClo) {
        yield fork(
          handleGetAvailableTelReportSaga,
          { params: telParams, currentBranchGuid, routeGuid, initRouteGuid: routeGuid },
        );
      }
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleAvailableReportRequestSaga exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleSaveRouteRequest({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const {
      withRefresh,
      currentRoute,
      withCloseModal,
      onSaveCallback,
      withCloseOnSave,
      currentRouteItems,
      currentRouteContainers,
    } = payload;

    const openedFromPage = yield select(makeSelectOpenedFromPage());
    const telPrimaryRefAuto = yield select(makeSelectPrimaryReferenceAutogeneratedConfig());
    const initialRoute = yield select(makeSelectCurrentRouteInitialState());

    let data = H.transformCurrentRouteForRequest(
      currentRoute,
      currentRouteItems,
      telPrimaryRefAuto,
      initialRoute,
      currentRouteContainers,
    );

    if (G.isNotNilAndNotEmpty(onSaveCallback)) {
      data = R.assoc(GC.SYSTEM_LIST_TELS, R.map(R.omit([GC.FIELD_RATE]), data.tels), data);
    }

    if (G.isNilOrEmpty(data[GC.FIELD_BRANCH_GUID])) {
      data = R.assoc(
        GC.FIELD_BRANCH_GUID,
        yield select(makeSelectCurrentBranchGuid()),
        data,
      );
    }

    const isNotNewRoute = G.isNotNilAndNotEmpty(R.prop(GC.FIELD_GUID, data));
    const method = G.ifElse(isNotNewRoute, 'put', 'post');

    const res = yield call(sendRequest, method, endpointsMap.route, { data });

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.resetScheduleHistory());

      const localeKey = G.ifElse(
        G.isNilOrEmpty(R.prop(GC.FIELD_GUID, data)),
        'toasts:success:create',
        'toasts:success:update',
      );

      const { splittedLoadGuid } = yield select(makeSelectSplittedLoadOptions());

      if (R.and(G.isTrue(withCloseOnSave), G.isNotNilAndNotEmpty(splittedLoadGuid))) {
        G.goToRoute(routesMap.dispatchDetailsLoad(splittedLoadGuid));

        return yield put(closeLoader());
      }

      if (R.equals(openedFromPage, 'DISPATCH_DETAILS_NEW')) {
        yield put(setDispatchPlannerOpened(false));
        yield put(A.setOpenedFromPage(null));
        yield put(getLoadDetailsRequest());

        return yield put(closeLoader());
      }

      if (withCloseModal) yield put(closeModal());

      if (G.isNotNilAndNotEmpty(onSaveCallback)) yield put(onSaveCallback());

      if (G.isTrue(withRefresh)) {
        yield put(A.setPreviousRouteState(currentRoute));
        yield put(A.setInitialRouteGuid(res.data));
        yield put(A.getAvailableReportsRequest());
      }

      if (G.isTrue(withCloseOnSave)) {
        const report = yield select(makeSelectTelReport());

        yield put(A.setCurrentTelReportWithRoute(report));

        const branchGuid = yield select(makeSelectCurrentBranchGuid());

        yield put(A.addNewTelToRouteBuilder({
          [GC.FIELD_BRANCH_GUID]: branchGuid,
          primaryReference: { value: `${G.getWindowLocale('titles:tel', 'Trip')}-1` },
        }));

        yield put(A.getTelNextPage(true));
      }

      yield put(closeLoader());

      yield call(G.showToastrMessage, 'success', localeKey);
    } else {
      yield call(G.handleFailResponse, res, 'handleSaveRouteRequest fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleSaveRouteRequest exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleSaveDraftRouteRequest({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const { currentRoute, currentRouteItems } = payload;

    let data = H.transformCurrentRouteForRequest(currentRoute, currentRouteItems);

    const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

    if (G.isNilOrEmpty(data[GC.FIELD_BRANCH_GUID])) {
      data = R.assoc(
        GC.FIELD_BRANCH_GUID,
        currentBranchGuid,
        data,
      );
    }

    data = R.assoc(GC.FIELD_DRAFT_BRANCH_GUID, currentBranchGuid, data);
    const method = G.ifElse(G.isNilOrEmpty(R.prop('draftGuid', currentRoute)), 'post', 'put');

    const res = yield call(sendRequest, method, endpointsMap.draftRoute, { data });

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.resetScheduleHistory());

      const localeKey = G.ifElse(
        G.isNilOrEmpty(R.prop('draftGuid', currentRoute)),
        'toasts:success:create',
        'toasts:success:update',
      );
      yield call(G.showToastrMessage, 'success', localeKey);
    } else {
      yield call(G.handleFailResponse, res, 'handleSaveDraftRouteRequest fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleSaveDraftRouteRequest exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleDeleteDraft({ payload }) {
  try {
    const res = yield call(sendRequest, 'delete', endpointsMap.draftRoute, { params: { draftRouteGuid: payload } });

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'toasts:success:remove');
    } else {
      yield call(G.handleFailResponse, res, 'handleDeleteDraft fail');
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleDeleteDraft exception');
  }
}

function* handleRecalculateTelDistances({ payload }) {
  try {
    const currentRoute = yield select(makeSelectCurrentRoute());

    const currentTel = R.path([GC.SYSTEM_LIST_TELS, payload], currentRoute);
    const loadType = R.prop(GC.FIELD_LOAD_TYPE, currentTel);
    const stopPoints = H.getLoadEditModeStopsGeodata(currentTel);

    if (R.gt(R.length(stopPoints), 1)) {
      const options = {
        data: {
          stopPoints,
          [GC.FIELD_BRANCH_GUID]: currentTel[GC.FIELD_BRANCH_GUID],
        },
      };

      const res = yield call(sendRequest, 'post', endpointsMap.distanceCalculation, options);

      const { data, status } = res;

      if (R.isNil(R.prop(['stopResults'], data))) return yield put(closeLoader());

      const events = H.mapLoadEventsWithDistances(currentTel, loadType, data);

      if (G.isResponseSuccess(status)) {
        yield put(A.recalculateTelDistancesSuccess({
          events,
          guid: currentTel.guid,
        }));
      }
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleRecalculateTelDistances exception');
  }
}

function* handleUpdateStopOnRouteBuilderSaga({ payload }) {
  const { stop, telGuid } = payload;

  yield put(A.updateStopOnRouteBuilderTelSuccess(stop));

  if (G.isStopTypeTerminal(R.prop(GC.FIELD_STOP_TYPE, stop))) {
    const currentRoute = yield select(makeSelectCurrentRoute());
    const telGuids = R.keys(R.prop(GC.SYSTEM_LIST_TELS, currentRoute));

    yield all(telGuids.map((guid) => put(A.recalculateTelDistancesRequest(guid))));
  } else {
    yield put(A.recalculateTelDistancesRequest(telGuid));
  }
}

function* handleMoveTelStop({ guid, routeBuilderStore }) {
  try {
    const currentRoute = R.path(['currentRoute'], routeBuilderStore);
    const currentTel = R.path([GC.SYSTEM_LIST_TELS, guid], currentRoute);
    const loadType = R.prop(GC.FIELD_LOAD_TYPE, currentTel);
    const stopPoints = H.getLoadEditModeStopsGeodata(currentTel);

    if (G.isNilOrEmpty(stopPoints)) return true;

    if (R.equals(R.length(stopPoints), 1)) {
      const events = H.mapLoadEventsWithDistances(currentTel, loadType, { stopResults: [] });

      yield put(A.recalculateTelDistancesWithMoveItemSuccess({
        events,
        routeBuilderStore,
        [GC.FIELD_GUID]: G.getGuidFromObject(currentTel),
      }));
    } else {
      const options = {
        data: {
          stopPoints,
          [GC.FIELD_BRANCH_GUID]: G.getBranchGuidFromObject(currentTel),
        },
      };

      const res = yield call(sendRequest, 'post', endpointsMap.distanceCalculation, options);

      const { data, status } = res;

      const stopResults = R.pathOr([], ['stopResults'], data);
      const events = H.mapLoadEventsWithDistances(currentTel, loadType, { stopResults });

      if (G.isResponseSuccess(status)) {
        yield put(A.recalculateTelDistancesWithMoveItemSuccess({
          events,
          routeBuilderStore,
          [GC.FIELD_GUID]: G.getGuidFromObject(currentTel),
        }));
      }
    }
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'handleMoveTelStop exception');
  }
}

function* handleRecalculateTelDistancesWithMoveStop({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    let routeBuilderStore = yield select(makeSelectBuilderStore());

    if (H.isEqualSourceAndDestinationLoad(payload)) {
      routeBuilderStore = H.reorderTelEventsInState(routeBuilderStore, payload);

      yield call(handleMoveTelStop, {
        routeBuilderStore,
        [GC.FIELD_GUID]: R.path([GC.FIELD_SOURCE, 'droppableId'], payload),
      });
    } else {
      routeBuilderStore = H.moveEventFromTelToTel(routeBuilderStore, payload);

      const isSourceTelWithoutEvents = yield call(handleMoveTelStop, {
        routeBuilderStore,
        [GC.FIELD_GUID]: R.path([GC.FIELD_SOURCE, 'droppableId'], payload),
      });

      if (R.not(isSourceTelWithoutEvents)) routeBuilderStore = yield select(makeSelectBuilderStore());

      yield call(handleMoveTelStop, {
        routeBuilderStore,
        [GC.FIELD_GUID]: R.path([GC.FIELD_DESTINATION, 'droppableId'], payload),
      });
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleRecalculateTelDistancesWithMoveStop');
  } finally {
    yield put(closeLoader());
  }
}

function* handleChangeDefaultCloReportSaga({ payload }) {
  try {
    yield put(openLoader());

    const res = yield call(sendRequest, 'put', endpointsMap.changeDefaultReport, { data: payload });

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

      const params = {
        reportType: GC.CLO_REPORT,
        [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      };

      yield call(
        handleGetAvailableCloReportSaga,
        {
          params,
          routeGuid: null,
          currentBranchGuid,
        },
      );
    } else {
      yield call(G.handleFailResponse, res, 'handleChangeDefaultCloReportSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleChangeDefaultCloReportSaga exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleChangeDefaultTelReportSaga({ payload }) {
  try {
    yield put(openLoader());

    const res = yield call(sendRequest, 'put', endpointsMap.changeDefaultReport, { data: payload });

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      const currentBranchGuid = yield select(makeSelectCurrentBranchGuid());

      const params = {
        reportType: GC.TEL_REPORT,
        [GC.FIELD_CURRENT_BRANCH]: currentBranchGuid,
      };

      yield call(
        handleGetAvailableTelReportSaga,
        {
          params,
          routeGuid: null,
          currentBranchGuid,
        },
      );
    } else {
      yield call(G.handleFailResponse, res, 'handleChangeDefaultTelReportSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleChangeDefaultTelReportSaga exception');
  } finally {
    yield put(closeLoader());
  }
}

function* getConfigsByNamesSaga({ payload }) {
  try {
    const { names, branchGuid } = payload;

    const options = {
      params: { names, [GC.FIELD_BRANCH_GUID]: branchGuid },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.branchConfigsEndpoint, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const mappedConfigs = G.mapConfigValuesByName(data);

      yield call(G.setAmousConfigToWindow, mappedConfigs);

      const defaultCrossDocks = R.path(
        ['configs', GC.TEL_ROUTE_BUILDER_DEFAULT_CROSSDOCK, GC.FIELD_VALUE],
        mappedConfigs,
      );

      if (G.isNotNilAndNotEmpty(defaultCrossDocks)) {
        yield put(A.setInboundTerminals(defaultCrossDocks));
        yield put(A.setOutboundTerminals(defaultCrossDocks));
      }

      yield put(A.getConfigsByNamesSuccess(mappedConfigs));
    } else {
      yield call(G.handleFailResponse, res, 'getConfigsByNamesSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'getConfigsByNamesSaga exception');
  }
}

function* handleDispatchTelRateSaga({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const { guid, dispatchAction } = payload;

    const endpoint = endpointsMap.getTelActionEndpoint(guid, dispatchAction);

    const res = yield call(sendRequest, 'put', endpoint);

    const { status} = res;

    if (G.isResponseSuccess(status)) {
      yield call(resetScheduleHistoryAfterExternalMutation);

      const currentRoute = yield select(makeSelectCurrentRoute());

      yield put(A.setInitialRouteGuid(currentRoute[GC.FIELD_GUID]));
      yield put(A.getAvailableReportsRequest());
    } else {
      yield call(G.handleFailResponse, res, 'handleDispatchTelRateSaga fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleDispatchTelRateSaga exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleDispatchCarrierTelRateSaga({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const endpoint = endpointsMap.telCarrierRateDispatch;

    const res = yield call(sendRequest, 'put', endpoint, { data: payload });

    const { status } = res;

    yield put(closeModal());

    if (G.isResponseSuccess(status)) {
      yield call(resetScheduleHistoryAfterExternalMutation);

      const currentRoute = yield select(makeSelectCurrentRoute());

      yield put(A.setInitialRouteGuid(currentRoute[GC.FIELD_GUID]));
      yield put(A.getAvailableReportsRequest());
    } else {
      yield call(G.handleFailResponse, res, 'handleDispatchCarrierTelRateSaga fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleDispatchCarrierTelRateSaga  exception');
  } finally {
    yield put(closeLoader());
  }
}

function* handleAddNewStopToRouteBuilderTel({ payload }) {
  try {
    yield put(A.addNewStopToRouteBuilderTelSuccess(payload));
    yield put(A.recalculateTelDistancesRequest(G.getPropFromObject(GC.FIELD_LOAD_GUID, payload)));
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleAddNewStopToRouteBuilderTel  exception');
  }
}

// route template
function* handleSaveAsTemplateSaga({ payload }) {
  try {
    yield put(openLoader({ showDimmer: true }));

    const { callback, templateGuid, deleteOriginalRoute } = payload;

    const method = G.ifElse(
      G.isNotNilAndNotEmpty(templateGuid),
      'put',
      'post',
    );

    const res = yield call(sendRequest, method, endpointsMap.routeTemplate, { data: payload });

    const { status } = res;

    yield put(closeModal());

    if (G.isResponseSuccess(status)) {
      yield call(G.showToastrMessage, 'success', 'toasts:success:create');

      if (R.and(R.not(G.isFunction(callback)), deleteOriginalRoute)) {
        yield call(G.goToRoute, GC.ROUTE_PATH_TEMPLATES_ROUTE_LIST);
      }
    } else {
      yield call(G.handleFailResponse, res, 'handleSaveAsTemplateSaga fail');
    }
  } catch (error) {
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleSaveAsTemplateSaga  exception');
  } finally {
    yield put(closeLoader());
  }
}

const CONFIGS_ARR = [
  // trailer
  GC.TRAILER_MANAGE_TRAILERS_FROM,
  GC.TRAILER_MANAGE_TRAILERS_PER_EVENT,
  // clo
  GC.CLO_GENERAL_USE_CONTAINERS,
  GC.CLO_RATE_SYNC_ITEMS_WEIGHT,
  GC.CLO_GENERAL_USE_LINKED_ORDERS,
  // tel
  GC.TEL_RATE_SYNC_ITEMS_WEIGHT,
  GC.TEL_PRIMARY_REFERENCE_TYPE,
  GC.TEL_PRIMARY_REFERENCE_PREFIX,
  GC.TEL_PRIMARY_REFERENCE_SEQUENCE,
  GC.TEL_PRIMARY_REFERENCE_COPY_FROM_CLO,
  GC.TEL_PRIMARY_REFERENCE_AUTOGENERATED,
  GC.TEL_ROUTE_BUILDER_DEFAULT_CROSSDOCK,
  GC.TEL_ROUTE_BUILDER_DISABLE_TIME_VALIDATION,
  // general
  GC.GENERAL_SERVICES,
  GC.GENERAL_EQUIPMENTS,
  GC.GENERAL_MODE_TRANSPORTATION,
  GC.GENERAL_DRAFT_ROUTE_NAME_PREFIX,
  GC.GENERAL_ROUTE_NAME_AUTOGENERATED,
  GC.GENERAL_DRAFT_ROUTE_NAME_SEQUENCE,
  GC.GENERAL_TRANSPORTATION_SERVICE_TYPE,
  GC.GENERAL_UOM_CALC_DEFAULT_UOM_SYSTEM,
  GC.GENERAL_DRAFT_ROUTE_NAME_AUTOGENERATED,
];

function* handleGetRouteBuilderSaga({ payload }) {
  while (true) { // eslint-disable-line
    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    yield put(openLoader({ showDimmer: true }));

    yield put(A.getConfigsByNamesRequest({
      branchGuid,
      names: R.join(',', CONFIGS_ARR),
    }));

    yield put(A.getAvailableReportsRequest({ onlyClo: true }));

    yield take(A.getAvailableCloReportSuccess);

    yield put(A.getCurrentRouteByTelGuidRequest(payload));
    yield put(closeLoader());
    break;
  }
}

function* handleGetCrossDockLocationsSaga(branchGuid) {
  try {
    const options = {
      params: { [GC.FIELD_BRANCH_GUID]: branchGuid },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.crossDockLocationList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getCrossDockLocationsSuccess(data));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetCrossDockLocationsSaga fail');
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleGetCrossDockLocationsSaga exception');
  }
}

function* handleAddTelToPlannerByGuid({ payload }) {
  try {
    const currentRoute = yield select(makeSelectCurrentRoute());

    if (G.isNotNilAndNotEmpty(R.path([GC.SYSTEM_LIST_TELS, payload], currentRoute))) return;

    const telList = yield select(makeSelectTelList());
    const exists = R.find(
      R.propEq(payload, GC.FIELD_GUID),
      R.or(telList, []),
    );

    if (R.isNil(exists)) {
      yield call(getReportTelByGuid, { payload });
    }

    yield put(A.addTelToPlanner(payload));
  } catch (error) {
    yield call(G.handleException, error, 'handleAddTelToPlannerByGuid exception');
  }
}

function* handleVisitPageSaga({ payload }) {
  while (true) { // eslint-disable-line
    yield call(visitPageSaga, payload, GC.CHECK_VISIT_ROUTE_BUILDER_V2_PAGE);

    yield put(openLoader({ showDimmer: true }));

    const page = R.path([GC.FIELD_LOCATION, 'pathname'], window);

    yield put(getAccessorialForConfigRequest());

    if (R.equals(page, GC.ROUTE_PATH_ROUTE_BUILDER)) {
      yield put(A.setOpenedFromPage(null));
    }

    const currentRouteItems = yield select(makeSelectCurrentRouteItems());
    const routeChanged = yield select(makeSelectRouteChanged());
    const routeGuid = yield select(makeSelectInitRouteGuid());

    if (G.isAllTrue(G.isNilOrEmpty(routeGuid), routeChanged, G.isNotNilAndNotEmpty(currentRouteItems))) {
      yield put(closeLoader());

      return;
    }

    yield put(A.resetAllListsAndPaginations());

    const branchGuid = yield select(makeSelectCurrentBranchGuid());

    yield call(getConfigsByNamesSaga, { payload: {
      branchGuid,
      names: R.join(',', CONFIGS_ARR),
    } });

    yield call(handleAvailableReportRequestSaga, { payload: null });

    const currentRoute = yield select(makeSelectCurrentRoute());

    if (G.isNilOrEmpty(R.values(R.pathOr({}, [GC.SYSTEM_LIST_TELS], currentRoute)))) {
      const autoTelPrimaryRef = yield select(makeSelectPrimaryReferenceAutogeneratedConfig());
      const routeGuid = yield select(makeSelectInitRouteGuid());

      if (R.and(G.isTrue(autoTelPrimaryRef), G.isNilOrEmpty(routeGuid))) {
        yield put(A.addNewTelToRouteBuilder({
          [GC.FIELD_BRANCH_GUID]: branchGuid,
          primaryReference: { value: `${G.getWindowLocale('titles:tel', 'Trip')}-1` },
        }));
      }
    }

    yield call(handleGetCrossDockLocationsSaga, branchGuid);

    const { eventIndex, splittedLoadGuid } = yield select(makeSelectSplittedLoadOptions());

    if (G.isNotNilAndNotEmpty(splittedLoadGuid)) {
      const currentRoute = yield select(makeSelectCurrentRoute());

      yield put(A.setCurrentRouteTels(
        H.getCurrentRouteTelsWithSplitEvents({ eventIndex, currentRoute, splittedLoadGuid }),
      ));
    }

    yield put(getAllAvailableCloRefTypesRequest());
    yield put(getAllAvailableTelRefTypesRequest());

    yield put(closeLoader());

    break;
  }
}

function* handleOpenDispatchPlannerSaga() {
  try {
    yield put({
      payload: {
        route: GC.ROUTE_PATH_ROUTE_BUILDER,
      },
      type: GC.VISIT_ROUTE_BUILDER_V2_PAGE,
    });
  } catch (error) {
    yield call(G.handleException, error, 'handleOpenDispatchPlannerSaga exception');
  }
}

function* dispatchPlannerV2WatcherSaga() {
  yield takeLatest(GC.VISIT_ROUTE_BUILDER_V2_PAGE, handleVisitPageSaga);
  //
  yield takeLatest(A.deleteDraft, handleDeleteDraft);
  yield takeLatest(A.getCloNextPage, handleGetCloListSaga);
  yield takeLatest(A.getTelNextPage, handleGetTelListSaga);
  yield takeLatest(A.getReportTelByGuid, getReportTelByGuid);
  yield takeLatest(A.toggleCloDetails, handleToggleCloDetails);
  yield takeLatest(A.toggleTelDetails, handleToggleTelDetails);
  yield takeLatest(A.saveRouteRequest, handleSaveRouteRequest);
  yield takeLatest(A.getRouteBuilder, handleGetRouteBuilderSaga);
  yield takeLatest(A.getDraftListRequest, handleGetDraftListSaga);
  yield takeLatest(A.getConfigsByNamesRequest, getConfigsByNamesSaga);
  yield takeLatest(A.getCloDetailsRequest, handleGetCloDetailsRequest);
  yield takeLatest(A.dispatchTelRateRequest, handleDispatchTelRateSaga);
  yield takeEvery(A.updateTelCustomStatusSuccess, resetScheduleHistoryAfterExternalMutation);
  yield takeLatest(A.saveDraftRouteRequest, handleSaveDraftRouteRequest);
  yield takeLatest(A.getCurrentRouteByGuidRequest, handleGetRouteByGuid);
  yield takeLatest(A.getInboundTelNextPage, handleGetInboundTelListSaga);
  yield takeLatest(A.selectCloForTelRequest, handleSelectCloForTelRequest);
  yield takeLatest(A.getOutboundTelNextPage, handleGetOutboundTelListSaga);
  yield takeLatest(A.addTelToPlanner, handleGetRouteByTelGuidAndMergeSaga);
  yield takeLatest(A.addTelToRouteBuilderByGuid, handleAddTelToPlannerByGuid);
  yield takeLatest(A.getCurrentRouteByTelGuidRequest, handleGetRouteByTelGuid);
  yield takeLatest(A.reconcileTripDetailsRequest, handleReconcileTripDetails);
  yield takeLatest(A.createCloReportRequest, handleCreateCloReportRequestSaga);
  yield takeLatest(A.updateCloReportRequest, handleUpdateCloReportRequestSaga);
  yield takeLatest(A.createTelReportRequest, handleCreateTelReportRequestSaga);
  yield takeLatest(A.updateTelReportRequest, handleUpdateTelReportRequestSaga);
  yield takeLatest(A.openDispatchPlannerRequest, handleOpenDispatchPlannerSaga);
  yield takeLatest(A.getAvailableReportsRequest, handleAvailableReportRequestSaga);
  yield takeEvery(A.recalculateTelDistancesRequest, handleRecalculateTelDistances);
  yield takeLatest(A.refreshCloListWithRouteClos, handleRefreshCloListWithRouteClos);
  yield takeLatest(A.addNewStopToRouteBuilderTel, handleAddNewStopToRouteBuilderTel);
  yield takeLatest(A.getDraftRouteByDraftGuidRequest, handleGetDraftRouteByDraftGuid);
  yield takeLatest(A.changeDefaultCloReportRequest, handleChangeDefaultCloReportSaga);
  yield takeLatest(A.changeDefaultTelReportRequest, handleChangeDefaultTelReportSaga);
  yield takeLatest(A.dispatchCarrierTelRateRequest, handleDispatchCarrierTelRateSaga);
  yield takeLatest(A.updateStopOnRouteBuilderTelRequest, handleUpdateStopOnRouteBuilderSaga);
  yield takeLatest(A.recalculateTelDistancesWithMoveStopRequest, handleRecalculateTelDistancesWithMoveStop);
  // cross dock clos
  yield takeLatest(A.getCrossDockCloNextPage, handleGetCrossDockCloListSaga);
  yield takeLatest(A.selectCrossDockCloRequest, handleSelectCrossDockCloSaga);
  yield takeLatest(A.toggleCrossDockCloDetails, handleToggleCrossDockCloDetails);
  yield takeLatest(A.refreshCrossDockCloListWithRouteClos, handleRefreshCrossDockCloListWithRouteClos);
  // route template
  yield takeLatest(A.saveAsTemplateRequest, handleSaveAsTemplateSaga);
}

export default dispatchPlannerV2WatcherSaga;
